import { differenceInCalendarDays, startOfYear } from 'date-fns'
import { createDateWithoutTimezone } from '@omnea-app/packages/universal/date-utils/createDateWithoutTimezone'
import { MAX_BUCKETS } from '@omnea-app/packages/universal/reporting/widgetLimits'
import { type TimeRange, type ChartType, type WidgetTimeRange } from './insightsTypes'

export const GRANULARITY_OPTIONS = [
  { value: 'day', label: 'By day' },
  { value: 'week', label: 'By week' },
  { value: 'month', label: 'By month' },
  { value: 'quarter', label: 'By quarter' },
  { value: 'year', label: 'By year' },
] as const

export type TimeGranularity = (typeof GRANULARITY_OPTIONS)[number]['value']

export type GranularityOption = (typeof GRANULARITY_OPTIONS)[number]

export const DEFAULT_GRANULARITY: TimeGranularity = 'month'

// Approximate days per bucket, for estimating how many bars a span produces.
const BUCKET_DAYS: Record<TimeGranularity, number> = { day: 1, week: 7, month: 30, quarter: 91, year: 365 }

export const GRANULARITY_ORDER = GRANULARITY_OPTIONS.map((option) => option.value)

// A range's default is the finest bucket that keeps the chart within MAX_BUCKETS bars. Through fitsInBars
// the same cap sets the legibility floors of date breakdowns, so the axis and a date breakdown agree on
// how many buckets a span can take.
const fitsInBars = (days: number, granularity: TimeGranularity): boolean => days / BUCKET_DAYS[granularity] <= MAX_BUCKETS

// Which buckets are legible for a span of N days: day only up to a week, week up to a month, then month and
// progressively coarser buckets as the span grows. Presets and custom ranges both resolve to a span and run
// through this to decide what the axis offers and defaults to.
const granularitiesForSpan = (days: number): TimeGranularity[] => {
  const allowed = new Set<TimeGranularity>()
  if (days <= 7) allowed.add('day')
  else if (days <= 30) allowed.add('week')
  else {
    allowed.add('month')
    if (days >= 90) allowed.add('quarter')
    if (days >= 365) allowed.add('year')
  }
  return GRANULARITY_OPTIONS.filter((o) => allowed.has(o.value)).map((o) => o.value)
}

// Finest legible bucket whose bar count stays reasonable; an unbounded span (all time) falls to the coarsest.
const defaultGranularityForSpan = (days: number): TimeGranularity => {
  const available = granularitiesForSpan(days)
  return available.find((g) => fitsInBars(days, g)) ?? available.at(-1) ?? DEFAULT_GRANULARITY
}

export const coarsenToFitDays = (granularity: TimeGranularity, spanDays: number): TimeGranularity =>
  GRANULARITY_ORDER.slice(GRANULARITY_ORDER.indexOf(granularity)).find((candidate) => fitsInBars(spanDays, candidate)) ?? 'year'

export const customSpanDays = (range: { start: string; end: string }): number =>
  differenceInCalendarDays(createDateWithoutTimezone(range.end), createDateWithoutTimezone(range.start)) + 1

// Presets resolve to a span in days so they share the span logic. All time is unbounded; year-to-date grows
// through the year, so it's measured Jan 1 → today rather than a fixed length.
const FIXED_PRESET_SPAN_DAYS: Record<Exclude<TimeRange, 'all_time' | 'year_to_date'>, number> = {
  last_7_days: 7,
  last_30_days: 30,
  last_90_days: 90,
  last_12_months: 365,
}

const presetSpanDays = (range: TimeRange): number => {
  if (range === 'all_time') return Number.POSITIVE_INFINITY
  if (range === 'year_to_date') {
    const today = new Date()
    return differenceInCalendarDays(today, startOfYear(today)) + 1
  }
  return FIXED_PRESET_SPAN_DAYS[range]
}

const widgetSpanDays = (range: WidgetTimeRange): number =>
  typeof range === 'string' ? presetSpanDays(range) : customSpanDays(range)

// All time has no measurable span without querying the data's extent, so it takes month rather than
// reading as unbounded and flooring at year.
export const rangeLegibleFloor = (range: WidgetTimeRange): TimeGranularity =>
  range === 'all_time' ? 'month' : coarsenToFitDays('day', widgetSpanDays(range))

export const getAvailableGranularitiesForWidgetRange = (range: WidgetTimeRange): GranularityOption[] => {
  const allowed = granularitiesForSpan(widgetSpanDays(range))
  return GRANULARITY_OPTIONS.filter((opt) => allowed.includes(opt.value))
}

export const getDefaultGranularityForWidgetRange = (range: WidgetTimeRange): TimeGranularity =>
  defaultGranularityForSpan(widgetSpanDays(range))

// Pie never buckets; an explicit choice wins; otherwise ≥2 dimensions force time bucketing.
// The count is of dimensions resolveWidgetQuery actually produced, not of configured keys: a key
// that resolves to nothing (an unrecognised question type, no breakdown slot left, a level the
// members can't express, a number key with bucketing off) leaves the chart fewer dimensions to
// draw, and a time bucket forced for a dimension that isn't there splits the one that is.
export const computeEffectiveGranularity = (
  config: { chartType: ChartType; granularity?: TimeGranularity | null; timeRange: WidgetTimeRange },
  { dimensionCount, hasDateBreakdown }: { dimensionCount: number; hasDateBreakdown: boolean }
): TimeGranularity | null => {
  if (config.chartType === 'pie') return null
  // A date breakdown replaces the axis. A widget saved before the editor enforced that still holds an
  // axis beside one; it is ignored here rather than rewritten, so it returns once the date breakdown goes.
  // This must precede the count check: the count includes date dimensions, so it would add an axis.
  if (hasDateBreakdown) return null
  if (config.granularity != null) return config.granularity
  if (dimensionCount < 2) return null
  return getDefaultGranularityForWidgetRange(config.timeRange)
}
